import React, { useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Tabs, 
  Button, 
  Input, 
  Select, 
  Space, 
  Table, 
  Tag, 
  Progress,
  Statistic,
  Tooltip,
  List,
  Avatar,
  Divider
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  FileTextOutlined, 
  ClockCircleOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  ExperimentOutlined,
  BarChartOutlined,
  TeamOutlined,
  RocketOutlined,
  FileProtectOutlined
} from '@ant-design/icons';
import './index.css';

const { TabPane } = Tabs;
const { Option } = Select;

// 模拟实验数据
const experimentData = [
  {
    id: 'EXP20230001',
    name: '营销活动A/B测试',
    description: '针对不同营销策略的效果测试',
    status: 'running',
    progress: 65,
    type: 'abtest',
    createdTime: '2023-10-15',
    endTime: '2023-11-15',
    owner: '张三',
    collaborators: ['李四', '王五'],
    metrics: [
      { name: '转化率', value: '5.7%', trend: 'up' },
      { name: '点击率', value: '12.3%', trend: 'up' },
      { name: '平均停留时间', value: '2.5分钟', trend: 'down' }
    ]
  },
  {
    id: 'EXP20230002',
    name: '定价策略优化',
    description: '评估不同定价策略的市场反应',
    status: 'completed',
    progress: 100,
    type: 'multivariate',
    createdTime: '2023-09-01',
    endTime: '2023-10-01',
    owner: '李四',
    collaborators: ['赵六', '钱七'],
    metrics: [
      { name: '销售额', value: '¥45,600', trend: 'up' },
      { name: '转化率', value: '7.8%', trend: 'up' },
      { name: '客户满意度', value: '4.5/5', trend: 'same' }
    ]
  },
  {
    id: 'EXP20230003',
    name: '用户体验改进实验',
    description: '测试新UI设计对用户行为的影响',
    status: 'planned',
    progress: 0,
    type: 'abtest',
    createdTime: '2023-10-20',
    endTime: '2023-11-20',
    owner: '王五',
    collaborators: ['张三', '李四'],
    metrics: []
  },
  {
    id: 'EXP20230004',
    name: '渠道效率分析',
    description: '比较不同销售渠道的成本效益',
    status: 'draft',
    progress: 0,
    type: 'analysis',
    createdTime: '2023-10-18',
    endTime: '',
    owner: '赵六',
    collaborators: [],
    metrics: []
  }
];

// 模拟模型数据
const modelData = [
  {
    id: 'MDL20230001',
    name: '客户流失预测模型',
    description: '基于历史行为预测客户流失风险',
    accuracy: 87.5,
    status: 'deployed',
    lastTrainedTime: '2023-10-01',
    createdBy: '张三',
    type: '分类模型',
    features: ['购买频率', '最后交易时间', '客户价值', '客户历史', '投诉记录'],
    useCases: ['客户管理', '营销活动']
  },
  {
    id: 'MDL20230002',
    name: '销售预测模型',
    description: '基于多变量分析的销售趋势预测',
    accuracy: 92.3,
    status: 'testing',
    lastTrainedTime: '2023-10-10',
    createdBy: '李四',
    type: '回归模型',
    features: ['历史销售数据', '季节因素', '促销活动', '市场竞争情况'],
    useCases: ['库存管理', '销售规划']
  },
  {
    id: 'MDL20230003',
    name: '客户分群模型',
    description: '基于消费行为的客户分类',
    accuracy: 85.7,
    status: 'draft',
    lastTrainedTime: '2023-09-15',
    createdBy: '王五',
    type: '聚类模型',
    features: ['购买金额', '购买频率', '产品类别偏好', '客户年龄'],
    useCases: ['精准营销', '产品推荐']
  },
  {
    id: 'MDL20230004',
    name: '产品推荐引擎',
    description: '基于协同过滤的产品推荐系统',
    accuracy: 78.9,
    status: 'deployed',
    lastTrainedTime: '2023-08-20',
    createdBy: '赵六',
    type: '推荐系统',
    features: ['用户历史行为', '相似用户偏好', '产品特性'],
    useCases: ['线上商城', '营销邮件']
  }
];

// 模拟方案数据
const solutionData = [
  {
    id: 'SOL20230001',
    name: '全渠道营销优化方案',
    description: '整合线上线下渠道的营销资源优化方案',
    status: 'published',
    createdTime: '2023-09-10',
    author: '张三',
    department: '市场部',
    tags: ['营销', '渠道管理', '数据驱动'],
    models: ['客户分群模型', '销售预测模型'],
    experiments: ['营销活动A/B测试'],
    views: 128,
    likes: 45,
    comments: 12
  },
  {
    id: 'SOL20230002',
    name: '客户生命周期管理方案',
    description: '基于预测模型的客户全生命周期管理解决方案',
    status: 'draft',
    createdTime: '2023-10-05',
    author: '李四',
    department: '客户关系部',
    tags: ['CRM', '客户留存', '生命周期'],
    models: ['客户流失预测模型'],
    experiments: [],
    views: 56,
    likes: 23,
    comments: 5
  },
  {
    id: 'SOL20230003',
    name: '供应链库存优化方案',
    description: '利用预测模型优化库存管理降低成本',
    status: 'published',
    createdTime: '2023-08-15',
    author: '王五',
    department: '供应链部',
    tags: ['供应链', '库存管理', '成本优化'],
    models: ['销售预测模型'],
    experiments: ['定价策略优化'],
    views: 89,
    likes: 34,
    comments: 8
  }
];

// 实验列表表格列定义
const experimentColumns = [
  {
    title: '实验名称',
    dataIndex: 'name',
    key: 'name',
    render: (text, record) => (
      <div>
        <div className="table-item-title">{text}</div>
        <div className="table-item-desc">{record.description}</div>
      </div>
    )
  },
  {
    title: '类型',
    dataIndex: 'type',
    key: 'type',
    render: type => {
      let color = 'blue';
      let text = '未知';
      
      if (type === 'abtest') {
        color = 'blue';
        text = 'A/B测试';
      } else if (type === 'multivariate') {
        color = 'purple';
        text = '多变量测试';
      } else if (type === 'analysis') {
        color = 'green';
        text = '分析型实验';
      }
      
      return <Tag color={color}>{text}</Tag>;
    }
  },
  {
    title: '状态',
    dataIndex: 'status',
    key: 'status',
    render: status => {
      let color = 'default';
      let text = '未知';
      let icon = null;
      
      if (status === 'running') {
        color = 'processing';
        text = '进行中';
        icon = <SyncOutlined spin />;
      } else if (status === 'completed') {
        color = 'success';
        text = '已完成';
        icon = <CheckCircleOutlined />;
      } else if (status === 'planned') {
        color = 'warning';
        text = '计划中';
        icon = <ClockCircleOutlined />;
      } else if (status === 'draft') {
        color = 'default';
        text = '草稿';
        icon = <FileTextOutlined />;
      }
      
      return <Tag icon={icon} color={color}>{text}</Tag>;
    }
  },
  {
    title: '进度',
    dataIndex: 'progress',
    key: 'progress',
    render: progress => <Progress percent={progress} size="small" />
  },
  {
    title: '创建/结束时间',
    key: 'time',
    render: record => (
      <div>
        <div>{record.createdTime}</div>
        {record.endTime && <div>{record.endTime}</div>}
      </div>
    )
  },
  {
    title: '操作',
    key: 'action',
    render: () => (
      <Space size="small">
        <Button type="link" size="small">查看</Button>
        <Button type="link" size="small">编辑</Button>
      </Space>
    )
  }
];

// 模型列表表格列定义
const modelColumns = [
  {
    title: '模型名称',
    dataIndex: 'name',
    key: 'name',
    render: (text, record) => (
      <div>
        <div className="table-item-title">{text}</div>
        <div className="table-item-desc">{record.description}</div>
      </div>
    )
  },
  {
    title: '类型',
    dataIndex: 'type',
    key: 'type'
  },
  {
    title: '准确率',
    dataIndex: 'accuracy',
    key: 'accuracy',
    render: accuracy => `${accuracy}%`
  },
  {
    title: '状态',
    dataIndex: 'status',
    key: 'status',
    render: status => {
      let color = 'default';
      let text = '未知';
      let icon = null;
      
      if (status === 'deployed') {
        color = 'success';
        text = '已部署';
        icon = <RocketOutlined />;
      } else if (status === 'testing') {
        color = 'processing';
        text = '测试中';
        icon = <ExperimentOutlined />;
      } else if (status === 'draft') {
        color = 'default';
        text = '草稿';
        icon = <FileTextOutlined />;
      }
      
      return <Tag icon={icon} color={color}>{text}</Tag>;
    }
  },
  {
    title: '最后训练时间',
    dataIndex: 'lastTrainedTime',
    key: 'lastTrainedTime'
  },
  {
    title: '操作',
    key: 'action',
    render: () => (
      <Space size="small">
        <Button type="link" size="small">查看</Button>
        <Button type="link" size="small">部署</Button>
      </Space>
    )
  }
];

const DecisionLab = () => {
  const [activeTab, setActiveTab] = useState('experiments');
  
  // 处理搜索过滤
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // 过滤数据
  const getFilteredExperiments = () => {
    let filtered = experimentData;
    
    if (searchText) {
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(searchText.toLowerCase()) ||
        item.description.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(item => item.status === filterStatus);
    }
    
    return filtered;
  };
  
  const getFilteredModels = () => {
    let filtered = modelData;
    
    if (searchText) {
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(searchText.toLowerCase()) ||
        item.description.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(item => item.status === filterStatus);
    }
    
    return filtered;
  };
  
  const getFilteredSolutions = () => {
    let filtered = solutionData;
    
    if (searchText) {
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(searchText.toLowerCase()) ||
        item.description.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(item => item.status === filterStatus);
    }
    
    return filtered;
  };
  
  return (
    <div className="decision-lab">
      <div className="lab-header">
        <h1><ExperimentOutlined /> 决策实验室</h1>
        <p>基于数据科学方法设计实验、训练模型、制定决策方案</p>
      </div>
      
      <Row gutter={[16, 16]} className="lab-statistics">
        <Col xs={24} sm={8} md={8} lg={8} xl={8}>
          <Card>
            <Statistic 
              title="运行中实验" 
              value={experimentData.filter(e => e.status === 'running').length} 
              prefix={<SyncOutlined className="statistic-icon running" />} 
            />
          </Card>
        </Col>
        <Col xs={24} sm={8} md={8} lg={8} xl={8}>
          <Card>
            <Statistic 
              title="已部署模型" 
              value={modelData.filter(m => m.status === 'deployed').length} 
              prefix={<RocketOutlined className="statistic-icon deployed" />} 
            />
          </Card>
        </Col>
        <Col xs={24} sm={8} md={8} lg={8} xl={8}>
          <Card>
            <Statistic 
              title="发布方案" 
              value={solutionData.filter(s => s.status === 'published').length} 
              prefix={<FileProtectOutlined className="statistic-icon published" />} 
            />
          </Card>
        </Col>
      </Row>
      
      <Card className="lab-content">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane 
            tab={<span><ExperimentOutlined /> 实验管理</span>} 
            key="experiments"
          >
            <div className="tab-toolbar">
              <div className="tab-filters">
                <Input 
                  placeholder="搜索实验..." 
                  prefix={<SearchOutlined />} 
                  onChange={e => setSearchText(e.target.value)}
                  style={{ width: 250 }}
                />
                <Select 
                  defaultValue="all" 
                  style={{ width: 120 }} 
                  onChange={setFilterStatus}
                >
                  <Option value="all">所有状态</Option>
                  <Option value="running">进行中</Option>
                  <Option value="completed">已完成</Option>
                  <Option value="planned">计划中</Option>
                  <Option value="draft">草稿</Option>
                </Select>
              </div>
              <Button type="primary" icon={<PlusOutlined />}>新建实验</Button>
            </div>
            
            <Table 
              dataSource={getFilteredExperiments()} 
              columns={experimentColumns} 
              rowKey="id"
              pagination={{ pageSize: 5 }}
            />
          </TabPane>
          
          <TabPane 
            tab={<span><BarChartOutlined /> 模型管理</span>} 
            key="models"
          >
            <div className="tab-toolbar">
              <div className="tab-filters">
                <Input 
                  placeholder="搜索模型..." 
                  prefix={<SearchOutlined />} 
                  onChange={e => setSearchText(e.target.value)}
                  style={{ width: 250 }}
                />
                <Select 
                  defaultValue="all" 
                  style={{ width: 120 }} 
                  onChange={setFilterStatus}
                >
                  <Option value="all">所有状态</Option>
                  <Option value="deployed">已部署</Option>
                  <Option value="testing">测试中</Option>
                  <Option value="draft">草稿</Option>
                </Select>
              </div>
              <Button type="primary" icon={<PlusOutlined />}>新建模型</Button>
            </div>
            
            <Table 
              dataSource={getFilteredModels()} 
              columns={modelColumns} 
              rowKey="id"
              pagination={{ pageSize: 5 }}
            />
          </TabPane>
          
          <TabPane 
            tab={<span><FileProtectOutlined /> 方案管理</span>} 
            key="solutions"
          >
            <div className="tab-toolbar">
              <div className="tab-filters">
                <Input 
                  placeholder="搜索方案..." 
                  prefix={<SearchOutlined />} 
                  onChange={e => setSearchText(e.target.value)}
                  style={{ width: 250 }}
                />
                <Select 
                  defaultValue="all" 
                  style={{ width: 120 }} 
                  onChange={setFilterStatus}
                >
                  <Option value="all">所有状态</Option>
                  <Option value="published">已发布</Option>
                  <Option value="draft">草稿</Option>
                </Select>
              </div>
              <Button type="primary" icon={<PlusOutlined />}>新建方案</Button>
            </div>
            
            <List
              grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 3, xl: 3 }}
              dataSource={getFilteredSolutions()}
              renderItem={item => (
                <List.Item>
                  <Card 
                    hoverable 
                    className="solution-card"
                    actions={[
                      <Tooltip title="查看详情"><Button type="link" icon={<FileTextOutlined />} /></Tooltip>,
                      <Tooltip title="编辑"><Button type="link" icon={<FileTextOutlined />} /></Tooltip>,
                      <Tooltip title="分享"><Button type="link" icon={<TeamOutlined />} /></Tooltip>,
                    ]}
                  >
                    <Card.Meta
                      title={item.name}
                      description={item.description}
                    />
                    <Divider />
                    <div className="solution-info">
                      <div className="solution-tags">
                        {item.tags.map((tag, index) => (
                          <Tag key={index}>{tag}</Tag>
                        ))}
                      </div>
                      <div className="solution-meta">
                        <span><Avatar size="small" src={`https://joeschmoe.io/api/v1/${item.author}`} /> {item.author}</span>
                        <span>{item.createdTime}</span>
                      </div>
                      <div className="solution-stats">
                        <Tooltip title="查看次数">
                          <span><FileTextOutlined /> {item.views}</span>
                        </Tooltip>
                        <Tooltip title="点赞数">
                          <span><FileTextOutlined /> {item.likes}</span>
                        </Tooltip>
                        <Tooltip title="评论数">
                          <span><FileTextOutlined /> {item.comments}</span>
                        </Tooltip>
                      </div>
                    </div>
                  </Card>
                </List.Item>
              )}
            />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default DecisionLab; 